import React from 'react';
import clsx from 'clsx';

import { TableError } from './TableError';
import {
  Table as ShadTable,
  TableHeader as ShadTableHeader,
  TableBody as ShadTableBody,
  TableHead as ShadTableHead,
  TableRow as ShadTableRow,
  TableCell as ShadTableCell,
} from '../../ui/table';

// Column widths in pixels. Applied via <colgroup> with `table-layout: fixed`
// so the browser honors these as the source of truth for cell sizing.
const widths = {
  hidden: 0,
  xs: 60,
  sm: 100,
  md: 160,
  lg: 200,
  xl: 240,
  xxl: 360,
  full: 0, // 0 means "auto" — column grows to fill remaining space
};

const resolveWidth = (w: keyof typeof widths | number | undefined): number => {
  if (typeof w === 'number') return w;
  return widths[w ?? 'md'];
};

export interface EntryBase {
  insertedAt: string | Date;
  id: string;
}

export interface CellProps<Entry extends EntryBase> {
  entry: Entry;
  className?: string;
}

export type TableColumn<Entry extends EntryBase> = {
  title: string;
  /** Preset width keyword or a custom pixel value (e.g. `80`). */
  width?: keyof typeof widths | number;
  mobileLabel?: string;
  hideOnMobile?: boolean;
  isAction?: boolean;
  pin?: 'left' | 'right';
} & (
  | {
      field: keyof Entry;
    }
  | {
      field?: undefined;
      Cell?(props: CellProps<Entry>): React.ReactNode;
    }
);

export interface TableProps<Entry extends EntryBase> {
  data: Entry[];
  columns: TableColumn<Entry>[];
  pageSize: number;
  loading: boolean;
  dataType: string;
  error?: boolean;
  columnsClassName?: string;
  action?: React.ReactNode;
  title?: string;
  layout?: 'table' | 'cards' | 'responsive';
  textSm?: boolean;
  compactHeaders?: boolean;
}

// Compute left/right pixel offsets for contiguous pinned columns from each edge.
// Non-pinned columns terminate the accumulation, so only the edge cluster stacks.
const computePinOffsets = <Entry extends EntryBase>(cols: TableColumn<Entry>[]) => {
  const left: Record<number, number> = {};
  const right: Record<number, number> = {};
  let leftEdge = -1;
  let rightEdge = -1;

  let acc = 0;
  for (let i = 0; i < cols.length; i++) {
    if (cols[i].pin !== 'left') break;
    left[i] = acc;
    leftEdge = i;
    acc += resolveWidth(cols[i].width);
  }

  acc = 0;
  for (let i = cols.length - 1; i >= 0; i--) {
    if (cols[i].pin !== 'right') break;
    right[i] = acc;
    rightEdge = i;
    acc += resolveWidth(cols[i].width);
  }

  return { left, right, leftEdge, rightEdge };
};

const ScrollHint = ({ position }: { position: 'top' | 'bottom' }) => (
  <div
    className={clsx(
      'flex justify-center items-center gap-2 py-1 text-[10px] uppercase tracking-wider text-grey-500 dark:text-grey-500 bg-grey-100/50 dark:bg-blue-900/30',
      position === 'top'
        ? 'border-b border-grey-400 dark:border-grey-700'
        : 'border-t border-grey-400 dark:border-grey-700'
    )}
  >
    <span aria-hidden>&larr;</span>
    <span>Scroll for more details</span>
    <span aria-hidden>&rarr;</span>
  </div>
);

const isActionColumn = <Entry extends EntryBase>(col: TableColumn<Entry>): boolean => {
  if (col.isAction !== undefined) return col.isAction;
  const actionTitles = ['update', 'delete', 'edit', 'remove', 'details', 'view', 'action'];
  return actionTitles.some((a) => col.title.toLowerCase().includes(a));
};

const isVisibleOnMobile = <Entry extends EntryBase>(col: TableColumn<Entry>): boolean => {
  return !col.hideOnMobile && col.width !== 'hidden';
};

const DesktopTableView = <Entry extends EntryBase>({
  data,
  dataType,
  columns,
  pageSize,
  loading,
  error,
  columnsClassName,
  textSm,
  compactHeaders,
}: Omit<TableProps<Entry>, 'action' | 'title' | 'layout'>) => {
  const { left, right, leftEdge, rightEdge } = computePinOffsets(columns);

  const pinStyle = (i: number): React.CSSProperties | undefined => {
    if (left[i] !== undefined) return { left: left[i] };
    if (right[i] !== undefined) return { right: right[i] };
    return undefined;
  };

  // Section edges get a pseudo-element border that stays anchored to the
  // sticky cell (border-collapse would merge borders with neighbours, so they
  // scroll away with the neighbour).
  const leftEdgeRule =
    "after:content-[''] after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-grey-400 dark:after:bg-grey-700";
  const rightEdgeRule =
    "after:content-[''] after:absolute after:inset-y-0 after:left-0 after:w-px after:bg-grey-400 dark:after:bg-grey-700";

  // Inner pinned cells (not the edge of the pinned group) get a higher
  // z-index than the edge so that overflowing children — e.g. popovers
  // extending from Status into the Service column's screen space — paint
  // above the adjacent pinned cell instead of being clipped by it.
  const headerPinClass = (col: TableColumn<Entry>, i: number) => {
    if (col.pin === 'left') {
      const z = i === leftEdge ? 'z-20' : 'z-30';
      return clsx(
        `sticky ${z} bg-grey-100 dark:bg-blue-900`,
        i === leftEdge && leftEdgeRule
      );
    }
    if (col.pin === 'right') {
      const z = i === rightEdge ? 'z-20' : 'z-30';
      return clsx(
        `sticky ${z} bg-grey-100 dark:bg-blue-900`,
        i === rightEdge && rightEdgeRule
      );
    }
    return '';
  };

  const cellPinClass = (col: TableColumn<Entry>, i: number) => {
    if (col.pin === 'left') {
      const z = i === leftEdge ? 'z-10' : 'z-20';
      return clsx(
        `sticky ${z} bg-grey-200 dark:bg-blue-1000`,
        i === leftEdge && leftEdgeRule
      );
    }
    if (col.pin === 'right') {
      const z = i === rightEdge ? 'z-10' : 'z-20';
      return clsx(
        `sticky ${z} bg-grey-200 dark:bg-blue-1000`,
        i === rightEdge && rightEdgeRule
      );
    }
    return '';
  };

  const visibleColumns = columns.filter((col) => col.width !== 'hidden');

  return (
    <ShadTable
      className={clsx(
        'table-fixed border-collapse dark:text-blue-100',
        textSm ? 'text-sm' : 'text-base'
      )}
    >
      <colgroup>
        {visibleColumns.map((col, i) => {
          const w = resolveWidth(col.width);
          return (
            <col
              key={col.title + i}
              style={w > 0 ? { width: w } : undefined}
            />
          );
        })}
      </colgroup>

      <ShadTableHeader className="bg-grey-100 dark:bg-blue-900 text-grey-500 uppercase">
        <ShadTableRow className="border-b border-grey-400 dark:border-grey-700 hover:bg-transparent">
          {visibleColumns.map((col, i) => (
            <ShadTableHead
              key={col.title + i}
              scope="col"
              className={clsx(
                'px-2 text-left text-grey-500',
                compactHeaders ? 'text-xs' : 'text-sm',
                columnsClassName,
                headerPinClass(col, i)
              )}
              style={pinStyle(i)}
            >
              {col.title}
            </ShadTableHead>
          ))}
        </ShadTableRow>
      </ShadTableHeader>

      <ShadTableBody className="bg-grey-200 dark:bg-blue-1000">
        {error ? (
          <ShadTableRow className="hover:bg-transparent">
            <ShadTableCell colSpan={visibleColumns.length} className="min-h-[500px] text-center">
              <div className="flex justify-center items-center min-h-[500px]">
                <TableError />
              </div>
            </ShadTableCell>
          </ShadTableRow>
        ) : loading ? (
          Array.from({ length: pageSize }, (_, i) => (
            <ShadTableRow
              key={i}
              className="border-b border-grey-400 dark:border-grey-700 last:border-b-0 hover:bg-transparent"
            >
              <ShadTableCell colSpan={visibleColumns.length} className="h-[58px]">
                <div className="flex items-center w-full h-full animate-pulse">
                  <div className="w-full h-4 rounded bg-grey-400 dark:bg-grey-800"></div>
                </div>
              </ShadTableCell>
            </ShadTableRow>
          ))
        ) : data.length === 0 ? (
          <ShadTableRow className="hover:bg-transparent">
            <ShadTableCell colSpan={visibleColumns.length}>
              No <span>{dataType}</span> matched your search.
            </ShadTableCell>
          </ShadTableRow>
        ) : (
          data.map((entry, entryIndex) => (
            <ShadTableRow
              key={entry?.id || entryIndex}
              className="border-b border-grey-400 dark:border-grey-700 last:border-b-0 hover:bg-transparent"
            >
              {visibleColumns.map((col, columnIndex) => {
                const Cell = 'Cell' in col ? col.Cell : null;
                return (
                  <ShadTableCell
                    key={col.title + columnIndex}
                    className={clsx(
                      'px-2 py-3 break-words [&>a]:hover:text-blue-600 [&>a]:transition-colors',
                      columnsClassName,
                      cellPinClass(col, columnIndex)
                    )}
                    style={pinStyle(columnIndex)}
                  >
                    {Cell ? <Cell entry={entry} /> : <>{col.field ? entry[col.field] : null}</>}
                  </ShadTableCell>
                );
              })}
            </ShadTableRow>
          ))
        )}
      </ShadTableBody>
    </ShadTable>
  );
};

const MobileCardView = <Entry extends EntryBase>({
  data,
  dataType,
  columns,
  pageSize,
  loading,
  error,
}: Omit<TableProps<Entry>, 'action' | 'title' | 'layout' | 'columnsClassName'>) => {
  const visibleColumns = columns.filter(isVisibleOnMobile);
  const regularColumns = visibleColumns.filter((col) => !isActionColumn(col));
  const actionColumns = visibleColumns.filter((col) => isActionColumn(col));

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center w-full rounded-md border min-h-[300px] bg-grey-200 dark:bg-blue-1000 dark:border-grey-700 border-grey-400">
        <TableError />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: pageSize }, (_, i) => (
          <div
            key={i}
            className="p-4 rounded-md border bg-grey-200 dark:bg-blue-1000 dark:border-grey-700 border-grey-400"
          >
            {Array.from({ length: Math.min(regularColumns.length, 4) }, (_, j) => (
              <div
                key={j}
                className="flex justify-between items-center py-2 border-b last:border-b-0 dark:border-grey-700 border-grey-400"
              >
                <div className="w-20 h-3 rounded animate-pulse bg-grey-400 dark:bg-grey-800"></div>
                <div className="w-32 h-4 rounded animate-pulse bg-grey-400 dark:bg-grey-800"></div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex justify-center items-center py-8 rounded-md border bg-grey-200 dark:bg-blue-1000 dark:border-grey-700 border-grey-400">
        <p className="text-grey-600 dark:text-grey-400">
          No <span>{dataType}</span> matched your search.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {data.map((entry, entryIndex) => (
        <div
          key={entry?.id || entryIndex}
          className="overflow-hidden rounded-md border bg-grey-200 dark:bg-blue-1000 dark:border-grey-700 border-grey-400"
        >
          <div className="divide-y divide-grey-400 dark:divide-grey-700">
            {regularColumns.map((cell, columnIndex) => {
              const label = cell.mobileLabel || cell.title;
              const Cell = 'Cell' in cell ? cell.Cell : null;
              return (
                <div
                  key={cell.title + columnIndex}
                  className="flex gap-4 justify-between items-center px-4 py-3"
                >
                  <span className="text-xs font-medium uppercase text-grey-500 dark:text-grey-400 shrink-0">
                    {label}
                  </span>
                  <span className="text-right break-words min-w-0 [&>a]:hover:text-blue-600 [&>a]:transition-colors">
                    {Cell ? (
                      <Cell entry={entry} />
                    ) : (
                      <>{cell.field ? String(entry[cell.field] ?? '') : null}</>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          {actionColumns.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-end px-4 py-3 border-t border-grey-400 dark:border-grey-700">
              {actionColumns.map((cell, columnIndex) => {
                const Cell = 'Cell' in cell ? cell.Cell : null;
                return (
                  <div
                    key={cell.title + columnIndex}
                    className="[&>a]:hover:text-blue-600 [&>a]:transition-colors"
                  >
                    {Cell ? (
                      <Cell entry={entry} />
                    ) : (
                      <>{cell.field ? String(entry[cell.field] ?? '') : null}</>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const Table = <Entry extends EntryBase>({
  data,
  dataType,
  columns,
  pageSize,
  loading,
  error,
  action,
  columnsClassName,
  title,
  layout = 'responsive',
  textSm,
  compactHeaders,
}: TableProps<Entry>) => {
  const showDesktop = layout === 'table' || layout === 'responsive';
  const showMobile = layout === 'cards' || layout === 'responsive';

  // Grid wrapper with `minmax(0, 1fr)` constrains the single column to the
  // parent's width, allowing the inner ShadTable's overflow-x-auto to scroll
  // without consumers needing min-w-0 in their own layout chain.
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4 w-full">
      {(title || action) && (
        <div className="flex justify-between items-baseline">
          {title ? <h2 className="text-2xl font-bold">{title}</h2> : null}
          {action ? action : null}
        </div>
      )}

      {showDesktop && (
        <div
          className={clsx(
            'rounded-md overflow-hidden border border-grey-400 dark:border-grey-700 isolate',
            layout === 'responsive' ? 'hidden md:block' : ''
          )}
        >
          {columns.some((c) => c.pin === 'left' || c.pin === 'right') && (
            <ScrollHint position="top" />
          )}
          <DesktopTableView
            data={data}
            dataType={dataType}
            columns={columns}
            pageSize={pageSize}
            loading={loading}
            error={error}
            columnsClassName={columnsClassName}
            textSm={textSm}
            compactHeaders={compactHeaders}
          />
          {columns.some((c) => c.pin === 'left' || c.pin === 'right') && (
            <ScrollHint position="bottom" />
          )}
        </div>
      )}

      {showMobile && (
        <div className={clsx(layout === 'responsive' ? 'block md:hidden' : '')}>
          <MobileCardView
            data={data}
            dataType={dataType}
            columns={columns}
            pageSize={pageSize}
            loading={loading}
            error={error}
          />
        </div>
      )}
    </div>
  );
};
